import { Fragment, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import type { Grant } from '../lib/types';

interface RolesDoc {
  roles: Array<{ key: string; label: string; description: string }>;
  groups: Array<{
    group: string;
    capabilities: Array<{ key: string; label: string; grants: Record<string, Grant> }>;
  }>;
  rules: string[];
}

/**
 * What each role can do, read from the matrix the API actually enforces rather than written
 * out by hand. A permissions page that has drifted from the code is worse than none, so this
 * one is generated and cannot.
 */
export function RolesPage() {
  const { session } = useSession();
  const [doc, setDoc] = useState<RolesDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = !!session?.person.isAdmin;

  useEffect(() => {
    // Not fetched at all for anybody else — the API refuses it, and a failed request would
    // only produce a red error box where a plain sentence is the honest answer.
    if (!isAdmin) return;
    api
      .get<RolesDoc>('/access/roles')
      .then(setDoc)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the roles'));
  }, [isAdmin]);

  /**
   * Administrators only, decided here as well as in the API.
   *
   * Hiding the sidebar link is a courtesy; this is what stops somebody who types the address.
   * The API refuses it regardless, so this is about showing a sentence rather than a failure.
   */
  if (!isAdmin) {
    return (
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Roles and access</h1>
          <p className="subtitle">The permissions matrix is for administrators.</p>
        </div>
      </div>
    );
  }

  if (error) return <div className="callout">{error}</div>;
  if (!doc) return <div className="empty">Loading…</div>;

  const mine = 'admin';

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Roles and access</h1>
          <p className="subtitle">
            The grid below is read from the capabilities the API enforces, so it cannot drift
            from what actually happens. The notes under it are written by hand and are not
            checked against the code — one of them had drifted before anybody noticed.
          </p>
        </div>
      </div>

      <div className="grid grid-cards mb-2">
        {doc.roles.map((role) => (
          <div key={role.key} className="card card-pad">
            <div className="row-between" style={{ marginBottom: 8 }}>
              <h3>{role.label}</h3>
              {mine === role.key && <span className="badge badge-accent">you</span>}
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {role.description}
            </p>
          </div>
        ))}
      </div>

      <div className="card mb-2">
        <div className="card-head">
          <h2>What each role may do</h2>
          <span className="dim" style={{ fontSize: 12 }}>
            <strong>own</strong> means only what they raised or are assigned
          </span>
        </div>
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Can</th>
                {doc.roles.map((role) => (
                  <th key={role.key} className="right">
                    {role.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {doc.groups.map((group) => (
                // A fragment in a list still needs its key, or React cannot tell the groups apart.
                <Fragment key={group.group}>
                  <tr>
                    <td colSpan={doc.roles.length + 1} style={{ background: 'var(--surface-sunken)' }}>
                      <span className="stat-label">{group.group}</span>
                    </td>
                  </tr>
                  {group.capabilities.map((capability) => (
                    <tr key={capability.key}>
                      <td>
                        {capability.label.charAt(0).toUpperCase() + capability.label.slice(1)}
                        <div className="dim mono" style={{ fontSize: 10 }}>
                          {capability.key}
                        </div>
                      </td>
                      {doc.roles.map((role) => (
                        <td key={role.key} className="right">
                          <GrantMark grant={capability.grants[role.key]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Rules the grid does not show</h2>
        </div>
        <div className="card-pad">
          <ul className="muted" style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
            {doc.rules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function GrantMark({ grant }: { grant: Grant }) {
  if (grant === 'full') return <span style={{ color: 'var(--ok)', fontWeight: 700 }}>✓</span>;
  if (grant === 'own') return <span className="badge badge-warn">own</span>;
  return <span className="dim">—</span>;
}
